import React, { useState } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import AuthLayout from "@/layouts/AuthLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PasswordInput from "@/components/PasswordInput";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const user = await login(email, password);
      toast.success("Accesso effettuato");
      // Honor `?next=...` deep link from the session-expired redirect so the
      // operator returns exactly to where they were working.
      const nextParam = searchParams.get("next");
      const target = nextParam
        || location.state?.from
        || (user.onboarded ? "/app/dashboard" : "/onboarding");
      navigate(target, { replace: true });
    } catch (err) {
      // Backend signals an un-verified account with a structured 403 body so
      // we can bounce the user to the OTP screen without making them guess
      // why login failed.
      const data = err.response?.data;
      if (data?.code === "EMAIL_NOT_VERIFIED") {
        toast.info("Verifica la tua email per accedere.");
        navigate(`/verify-email?email=${encodeURIComponent(data.email || email)}`, { replace: true });
        return;
      }
      setError(formatApiError(data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title="Bentornata" subtitle="Accedi al tuo account">
      {searchParams.get("session") === "expired" && (
        <div
          className="mb-5 rounded-sm border px-4 py-3"
          style={{
            backgroundColor: "rgba(200,169,126,0.08)",
            borderColor: "rgba(200,169,126,0.4)",
          }}
          data-testid="login-session-expired-notice"
        >
          <div
            className="font-serif-heading italic"
            style={{ color: "#7B6A45", fontSize: "1.05rem", lineHeight: 1.3 }}
          >
            Sessione scaduta
          </div>
          <p className="text-xs mt-1 leading-relaxed" style={{ color: "#7B6A45" }}>
            Le ultime modifiche sono state salvate. Accedi di nuovo per continuare da dove avevi lasciato.
          </p>
        </div>
      )}
      <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
        <div>
          <Label htmlFor="email" className="label-uppercase">Email</Label>
          <Input
            id="email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-2 rounded-sm h-11"
            data-testid="login-email-input"
          />
        </div>
        <div>
          <div className="flex items-center justify-between">
            <Label htmlFor="password" className="label-uppercase">Password</Label>
            <Link to="/forgot-password" className="text-xs text-primary hover:underline" data-testid="login-forgot-link">
              Password dimenticata?
            </Link>
          </div>
          <PasswordInput
            id="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-2 rounded-sm h-11"
            testid="login-password-input"
            toggleTestid="login-password-toggle"
          />
        </div>
        {error && (
          <div className="text-sm text-destructive border border-destructive/30 bg-destructive/5 p-3 rounded-sm" data-testid="login-error">
            {error}
          </div>
        )}
        <Button type="submit" disabled={busy} className="w-full h-11 rounded-sm" data-testid="login-submit-button">
          {busy ? "Accesso in corso…" : "Accedi"}
        </Button>
      </form>
      <div className="mt-8 text-sm text-neutral-600">
        Non hai un account?{" "}
        <Link to="/register" className="text-primary hover:underline font-medium" data-testid="login-register-link">
          Registrati
        </Link>
      </div>
    </AuthLayout>
  );
}
